import {
	Button,
	Card,
	H1,
	H2,
	H3,
	Input,
	LineButton,
	Link,
	P,
	styled,
	th,
	useLinks,
	v,
} from "@veracity/vui";
import * as yup from "yup";
import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ForgotPasswordDto } from "../models/auto-generated/ForgotPasswordDto";
import { Page } from "../components/Page";
import { showErrorDialogAsync } from "../showErrorDialogAsync";
import { Ids } from "../components/Ids";
import { Label } from "../components/Label";
import { InputHelpText } from "../components/InputHelpText";
import { ReCAPTCHA } from "../components/ReCAPTCHA";
import { VeracityByDNV } from "../components/VeracityByDNV";
import { DNV } from "../components/DNV";
import { PaperAirplane } from "../components/PaperAirplane";
import { delayAsync } from "../utils/delayAsync";
import { fetchVeracityVoid } from "../services/fetchVeracity";
import { getCurrentReturnUrl } from "../utils/getCurrentReturnUrl";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const SLIDE_WIDTH = 387;

export function LostTotpPage() {
	let [state, setState] = useState<{
		step: ResetTotpSteps;
		emailAddress?: string;
	}>({ step: ResetTotpSteps.ForgotPassword });
	let [links] = useLinks();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [token, setToken] = useState("");
	const [dynamicAction, setDynamicAction] = useState("lostTotp");

	const handleReCaptchaVerify = useCallback(async () => {
		if (!executeRecaptcha) {
			console.log("Execute recaptcha not yet available");
			return;
		}

		const token = await executeRecaptcha(dynamicAction);
		setToken(token);
	}, [executeRecaptcha]);

	useEffect(() => {
		if (!executeRecaptcha || !dynamicAction) {
			return;
		}

		const handleReCaptchaVerify = async () => {
			const token = await executeRecaptcha(dynamicAction);
			setToken(token);
		};

		handleReCaptchaVerify();
	}, [executeRecaptcha, dynamicAction]);

	return (
		<Page p={1} bg="skyBlue.97" center>
			<Card
				column
				w={SLIDE_WIDTH}
				overflow="hidden"
				border="1px solid grey.40"
				userSelect="none"
			>
				<Slides
					style={{
						left:
							state.step === ResetTotpSteps.CheckYourEmail
								? -SLIDE_WIDTH + "px"
								: "0",
					}}
				>
					<v.div w={SLIDE_WIDTH} p="32px">
						<H3 as="h1">Reset TOTP</H3>
						<P mt={1}>Please type your email to reset TOTP.</P>
						<Formik
							initialValues={{
								emailAddress: "",
								reCaptchaToken: "",
							}}
							validationSchema={resetTotpSchema}
							onSubmit={async (values, { setFieldValue }) => {
								await handleReCaptchaVerify();

								try {
									await fetchVeracityVoid(
										`/api/users/lostTotp`,
										{
											method: "POST",
											body: JSON.stringify({
												Email: values.emailAddress,
												ReturnUrl:
													getCurrentReturnUrl(),
												ReCaptchaToken: token,
											} as ForgotPasswordDto),
											headers: {
												"Content-Type":
													"application/json",
											},
										}
									);
								} catch (e) {
									await new Promise<void>((r) =>
										queueMicrotask(r)
									);

									showErrorDialogAsync(
										"Error when requesting TOTP reset",
										e
									);

									return;
								}

								setState({
									step: ResetTotpSteps.CheckYourEmail,
									emailAddress: values.emailAddress,
								});
							}}
						>
							{({
								values,
								errors,
								touched,
								isSubmitting,
								handleSubmit,
								getFieldProps,
								setFieldValue,
							}) => {
								return (
									<form noValidate onSubmit={handleSubmit}>
										<v.fieldset
											p={0}
											disabled={
												isSubmitting ||
												state.step ===
													ResetTotpSteps.CheckYourEmail
											}
										>
											<Ids
												length={2}
												fn={(ids) => (
													<>
														<Label
														mt={4}
															htmlFor={ids[0]}
														>
															Enter your email
														</Label>
														<Input
															type="email"
															mt={1}
															maxW="400px"
															id={ids[0]}
															state={
																touched.emailAddress
																	? errors.emailAddress
																		? "error"
																		: "success"
																	: null
															}
															variant={
																touched.emailAddress
																	? errors.emailAddress
																		? "red"
																		: "green"
																	: null
															}
															disabled={
																isSubmitting ||
																state.step !==
																	ResetTotpSteps.ForgotPassword
															}
															inputProps={{
																autoComplete:
																	"email",
																"aria-invalid":
																	!!(
																		touched.emailAddress &&
																		errors.emailAddress
																	),
																"aria-errormessage":
																	touched.emailAddress &&
																	errors.emailAddress
																		? ids[1]
																		: "",
															}}
															{...getFieldProps(
																"emailAddress"
															)}
														/>
														{touched.emailAddress &&
															errors.emailAddress && (
																<InputHelpText
																	id={ids[1]}
																	color="hsl(357, 83%, 54%)"
																>
																	{
																		errors.emailAddress
																	}
																</InputHelpText>
															)}
													</>
												)}
											/>
											<P maxW={400} fontSize={11}>
												This site is protected by
												reCAPTCHA and the Google{" "}
												<Link
													fontSize={11}
													href="https://policies.google.com/privacy"
												>
													Privacy Policy
												</Link>{" "}
												and{" "}
												<Link
													fontSize={11}
													href="https://policies.google.com/terms"
												>
													Terms of Service
												</Link>{" "}
												apply.
											</P>
											<Button
												mt={3}
												type="submit"
												iconRight="falArrowRight"
												isLoading={isSubmitting}
											>
												Continue
											</Button>
										</v.fieldset>
									</form>
								);
							}}
						</Formik>
					</v.div>
					<v.div
						w={SLIDE_WIDTH}
						p={4}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						textAlign="center"
					>
						<PaperAirplane width={176.34782608695653} height={78} />
						<H3 as="h1" mt={2}>
							Check your e-mail
						</H3>
						<P mt={1} fontWeight="bold" whiteSpace="nowrap">
							{state.emailAddress}
						</P>
						<P mt={2}>
							If there's an account with that e-mail address, we
							will send you a confirmation link.
						</P>
						<LineButton mt={2} as="a" href={links.home}>
							Go to Veracity
						</LineButton>
					</v.div>
				</Slides>
				<v.footer
					bg="hsl(223, 67%, 18%)"
					p="24px 32px 32px"
					color="white"
				>
					<v.div display="flex" justifyContent="space-between">
						<VeracityByDNV />
						<DNV />
					</v.div>
					<P mt={2} fontSize="12px">
						Veracity is DNV's independent data platform and industry
						eco-system.
					</P>
					<P fontSize="12px">
						Visit{" "}
						<Link fontSize="12px" href={links.home} color="blue.40">
							www.dnv.com
						</Link>{" "}
						to discover more.
					</P>
				</v.footer>
			</Card>
		</Page>
	);
}

const Slides = styled.divBox`
	display: flex;
	position: relative;
	transition: left ${th.animation("standard")} 0.5s;
	width: ${SLIDE_WIDTH * 2}px;
`;

export const resetTotpSchema = yup.object().shape({
	emailAddress: yup.string().required("Please enter your e-mail address"),
});

enum ResetTotpSteps {
	ForgotPassword,
	CheckYourEmail,
}
